// =============================================================================
// Admin Dashboard Page
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  PlusIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui';
import { adminService, AdminDashboardStats, AuditLogEntry } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Component
// =============================================================================

export function AdminDashboardPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch dashboard stats (always)
      const dashboardStats = await adminService.getDashboard();
      setStats(dashboardStats);

      // Fetch audit log separately — only if user has permission
      if (hasPermission('auditLog', 'view')) {
        try {
          const auditResponse = await adminService.getAuditLog({ limit: 5 });
          setRecentActivity(auditResponse.events);
        } catch {
          // Silently skip — user may not have audit log access
        }
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getActivityIcon = (type: string) => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('login')) return <UsersIcon className="h-4 w-4" />;
    if (lowerType.includes('document')) return <DocumentTextIcon className="h-4 w-4" />;
    if (lowerType.includes('distribution')) return <BanknotesIcon className="h-4 w-4" />;
    if (lowerType.includes('profile') || lowerType.includes('investor')) return <ShieldCheckIcon className="h-4 w-4" />;
    if (lowerType.includes('security') || lowerType.includes('failed')) return <ExclamationTriangleIcon className="h-4 w-4" />;
    return <ClockIcon className="h-4 w-4" />;
  };

  const formatEventType = (type: string) => {
    return type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('dashboard', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your investor portal
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center gap-3 text-red-700">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="text-sm">Error loading dashboard: {error}</span>
            <button
              onClick={loadDashboardData}
              className="text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link to="/admin/investors">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 bg-blue-100 rounded-lg mb-2">
                <UsersIcon className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalInvestors ?? 0}</p>
              <p className="text-xs text-gray-500">Investors</p>
            </div>
          </Card>
        </Link>

        <Link to="/admin/deals">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 bg-purple-100 rounded-lg mb-2">
                <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeDeals ?? 0}</p>
              <p className="text-xs text-gray-500">Active Deals</p>
            </div>
          </Card>
        </Link>

        <Link to="/admin/documents">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 bg-yellow-100 rounded-lg mb-2">
                <DocumentTextIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.pendingDocuments ?? 0}</p>
              <p className="text-xs text-gray-500">Recent Docs</p>
            </div>
          </Card>
        </Link>

        <Link to="/admin/distributions">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 bg-green-100 rounded-lg mb-2">
                <BanknotesIcon className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.recentDistributions ?? 0}</p>
              <p className="text-xs text-gray-500">Recent Dists.</p>
            </div>
          </Card>
        </Link>

        <Link to="/admin/temp-access">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 bg-orange-100 rounded-lg mb-2">
                <ClockIcon className="h-5 w-5 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.tempAccessGrants ?? 0}</p>
              <p className="text-xs text-gray-500">Temp Access</p>
            </div>
          </Card>
        </Link>

        <Link to="/admin/audit-log">
          <Card padding="sm" hover>
            <div className="flex flex-col items-center text-center">
              <div
                className={`p-2 rounded-lg mb-2 ${
                  (stats?.securityAlerts ?? 0) > 0 ? 'bg-red-100' : 'bg-gray-100'
                }`}
              >
                <ShieldCheckIcon
                  className={`h-5 w-5 ${
                    (stats?.securityAlerts ?? 0) > 0 ? 'text-red-600' : 'text-gray-600'
                  }`}
                />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats?.securityAlerts ?? 0}</p>
              <p className="text-xs text-gray-500">Alerts</p>
            </div>
          </Card>
        </Link>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            {hasPermission('investors', 'create') && (
              <Link
                to="/admin/investors"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Add Investor</span>
              </Link>
            )}
            {hasPermission('documents', 'upload') && (
              <Link
                to="/admin/documents"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Upload Document</span>
              </Link>
            )}
            {hasPermission('distributions', 'create') && (
              <Link
                to="/admin/distributions"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <BanknotesIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">New Distribution</span>
              </Link>
            )}
            {hasPermission('deals', 'create') && (
              <Link
                to="/admin/deals"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Add Deal</span>
              </Link>
            )}
            {hasPermission('accounting', 'manage') && (
              <Link
                to="/admin/temp-access"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ClockIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Grant Temp Access</span>
              </Link>
            )}
            {hasPermission('auditLog', 'view') && (
              <Link
                to="/admin/audit-log"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ShieldCheckIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">View Audit Log</span>
              </Link>
            )}
          </div>
        </Card>

        {/* Recent Activity — only shown if user has auditLog.view */}
        {hasPermission('auditLog', 'view') && (
          <Card padding="md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <Link
                to="/admin/audit-log"
                className="text-sm text-primary-600 hover:text-primary-500 flex items-center gap-1"
              >
                View all
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>

            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClockIcon className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{formatEventType(activity.type)}</p>
                      {activity.userName && (
                        <p className="text-xs text-gray-500 truncate">{activity.userName}</p>
                      )}
                      {activity.userEmail && !activity.userName && (
                        <p className="text-xs text-gray-500 truncate">{activity.userEmail}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatTimeAgo(activity.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* System Status */}
      <Card padding="md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <div>
              <p className="text-sm font-medium text-gray-900">API</p>
              <p className="text-xs text-gray-500">Operational</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <div>
              <p className="text-sm font-medium text-gray-900">Database</p>
              <p className="text-xs text-gray-500">Operational</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <div>
              <p className="text-sm font-medium text-gray-900">Email Service</p>
              <p className="text-xs text-gray-500">Operational</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <div>
              <p className="text-sm font-medium text-gray-900">Storage</p>
              <p className="text-xs text-gray-500">Operational</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AdminDashboardPage;
